import React, { useState, useRef, useEffect } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { useAuth } from '../context/AuthContext';
import { useReggie } from '../context/ReggieContext';
import { Button } from './ui/button';
import { X, Send, Loader2, RefreshCw, Copy, Check } from 'lucide-react';

const API = process.env.REACT_APP_BACKEND_URL + '/api';
const REGGIE_IMG = 'https://customer-assets.emergentagent.com/job_email-magic-2/artifacts/ayib0ui5_ReggieRobot_Pondering.png';

export const ReggieChat = () => {
  const { session } = useAuth();
  const { isOpen, toggle, close, stepContext } = useReggie();

  // Don't render anything if user is not logged in
  if (!session) return null;

  return <ReggieChatInner session={session} isOpen={isOpen} toggle={toggle} close={close} stepContext={stepContext} />;
};

const ReggieChatInner = ({ session, isOpen, toggle, close, stepContext }) => {
  const [messages, setMessages] = useState([]);
  const [input, setInput] = useState('');
  const [loading, setLoading] = useState(false);
  const [lastContext, setLastContext] = useState(null);
  const [copied, setCopied] = useState(false);
  const messagesEndRef = useRef(null);
  const inputRef = useRef(null);

  useEffect(() => {
    if (isOpen && inputRef.current) {
      setTimeout(() => inputRef.current?.focus(), 200);
    }
  }, [isOpen]);

  useEffect(() => {
    messagesEndRef.current?.scrollIntoView({ behavior: 'smooth' });
  }, [messages]);

  const askReggie = async (userMessage, regenerate = false) => {
    const token = session?.access_token;
    if (!token) return;

    setLoading(true);
    const ctx = regenerate ? lastContext : stepContext;
    setLastContext(ctx);

    if (!regenerate) {
      setMessages(prev => [...prev, { role: 'user', content: userMessage }]);
    }

    try {
      const response = await fetch(`${API}/reggie/ask`, {
        method: 'POST',
        headers: {
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json'
        },
        body: JSON.stringify({
          message: userMessage,
          context: ctx
        })
      });

      if (response.ok) {
        const data = await response.json();
        if (regenerate) {
          setMessages(prev => {
            const updated = [...prev];
            updated[updated.length - 1] = { role: 'reggie', data };
            return updated;
          });
        } else {
          setMessages(prev => [...prev, { role: 'reggie', data }]);
        }
      } else {
        const err = await response.json().catch(() => ({}));
        setMessages(prev => [...prev, {
          role: 'reggie',
          data: {
            plain_english_explanation: err.detail || 'Something went wrong. Try again.',
            how_to_think_about_it: [],
            recommended_answer: '',
            next_step: ''
          }
        }]);
      }
    } catch (err) {
      setMessages(prev => [...prev, {
        role: 'reggie',
        data: {
          plain_english_explanation: 'Could not reach Reggie. Check your connection.',
          how_to_think_about_it: [],
          recommended_answer: '',
          next_step: ''
        }
      }]);
    } finally {
      setLoading(false);
    }
  };

  const handleSubmit = (e) => {
    e.preventDefault();
    if (!input.trim() || loading) return;
    const msg = input.trim();
    setInput('');
    askReggie(msg);
  };

  const handleRegenerate = () => {
    const lastUserMsg = [...messages].reverse().find(m => m.role === 'user');
    if (lastUserMsg) {
      askReggie(lastUserMsg.content, true);
    }
  };

  const handleUseThis = async (answer) => {
    // Extract actionable value via AI before inserting
    const token = session?.access_token;
    let extractedValue = answer;
    if (token) {
      try {
        const fieldLabel = stepContext?.current_question?.split(',')[0] || 'form field';
        const res = await fetch(`${API}/reggie/extract`, {
          method: 'POST',
          headers: { 'Authorization': `Bearer ${token}`, 'Content-Type': 'application/json' },
          body: JSON.stringify({ full_text: answer, field_label: fieldLabel })
        });
        if (res.ok) {
          const data = await res.json();
          extractedValue = data.extracted || answer;
        }
      } catch (err) {
        // Fallback to full text
      }
    }
    window.dispatchEvent(new CustomEvent('reggie-use-this', { detail: { answer: extractedValue } }));
    setCopied(true);
    setTimeout(() => setCopied(false), 2000);
  };

  const handleCopyAnswer = (answer) => {
    navigator.clipboard.writeText(answer);
    setCopied(true);
    setTimeout(() => setCopied(false), 2000);
  };

  return (
    <>
      {/* Floating launcher button */}
      <div className={`fixed bottom-5 right-5 z-40 flex flex-col items-center gap-1.5 transition-all duration-200 creataby-float ${isOpen ? 'scale-0 opacity-0' : 'scale-100 opacity-100'}`}>
        <span className="text-xs font-medium text-teal-400 bg-card/90 backdrop-blur-sm px-2.5 py-1 rounded-full border border-border shadow-sm">
          Ask Reggie
        </span>
        <button
          onClick={toggle}
          className="w-14 h-14 rounded-full shadow-lg hover:scale-110 focus:outline-none focus:ring-2 focus:ring-teal-500 transition-transform"
          data-testid="reggie-launcher"
          aria-label="Ask Reggie"
        >
          <img src={REGGIE_IMG} alt="Reggie" className="w-full h-full rounded-full border-2 border-teal-500" style={{objectFit: 'cover', objectPosition: 'center 15%'}} />
        </button>
      </div>

      {/* Chat panel */}
      <AnimatePresence>
        {isOpen && (
          <motion.div
            initial={{ opacity: 0, y: 20, scale: 0.95 }}
            animate={{ opacity: 1, y: 0, scale: 1 }}
            exit={{ opacity: 0, y: 20, scale: 0.95 }}
            transition={{ type: 'spring', damping: 25, stiffness: 300 }}
            className="fixed bottom-5 right-5 z-50 w-[420px] max-w-[calc(100vw-40px)] h-[560px] max-h-[calc(100vh-100px)] bg-card border border-border rounded-2xl shadow-2xl flex flex-col overflow-hidden"
            data-testid="reggie-panel"
          >
            {/* Header */}
            <div className="flex items-center gap-3 px-4 py-3 border-b border-border bg-secondary/30 flex-shrink-0">
              <img src={REGGIE_IMG} alt="Reggie" className="w-9 h-9 rounded-full border border-border" style={{objectFit: 'cover', objectPosition: 'center 15%'}} />
              <div className="flex-1 min-w-0">
                <p className="font-semibold text-foreground text-sm">Reggie</p>
                <p className="text-xs text-muted-foreground truncate">Your Creataby guide</p>
              </div>
              <button onClick={close} className="text-muted-foreground hover:text-foreground p-1" data-testid="reggie-close">
                <X className="h-4 w-4" />
              </button>
            </div>

            {/* Messages */}
            <div className="flex-1 overflow-y-auto px-4 py-3 space-y-3">
              {messages.length === 0 && (
                <div className="text-center py-8">
                  <img src={REGGIE_IMG} alt="Reggie" className="w-16 h-16 rounded-full mx-auto mb-3 border border-border" style={{objectFit: 'cover', objectPosition: 'center 15%'}} />
                  <p className="text-sm font-medium text-foreground mb-1">Hey! I'm Reggie.</p>
                  <p className="text-sm text-muted-foreground max-w-[280px] mx-auto">
                    Stuck on what to write? I can help you understand the question and draft an answer.
                  </p>
                </div>
              )}

              {messages.map((msg, i) => (
                <div key={`msg-${i}-${msg.role}`}>
                  {msg.role === 'user' ? (
                    <div className="flex justify-end">
                      <div className="bg-primary text-primary-foreground text-sm px-3 py-2 rounded-xl rounded-br-sm max-w-[85%]">
                        {msg.content}
                      </div>
                    </div>
                  ) : (
                    <div className="space-y-2.5">
                      {/* Explanation */}
                      {msg.data.plain_english_explanation && (
                        <div className="bg-secondary/60 rounded-xl px-3.5 py-3">
                          <p className="font-semibold text-sm text-teal-400 mb-1">What this means:</p>
                          <p className="text-foreground/90 text-sm leading-relaxed">{msg.data.plain_english_explanation}</p>
                        </div>
                      )}

                      {/* How to think about it */}
                      {msg.data.how_to_think_about_it?.length > 0 && (
                        <div className="bg-secondary/40 rounded-xl px-3.5 py-3">
                          <p className="font-semibold text-sm text-teal-400 mb-1.5">Think about:</p>
                          <ul className="space-y-1.5">
                            {msg.data.how_to_think_about_it.map((item, j) => (
                              <li key={`think-${j}-${item.slice(0,15)}`} className="text-sm text-foreground/80 flex items-start gap-1.5">
                                <span className="text-teal-400 mt-0.5 flex-shrink-0">-</span>
                                {item}
                              </li>
                            ))}
                          </ul>
                        </div>
                      )}

                      {/* Recommended answer */}
                      {msg.data.recommended_answer && (
                        <div className="bg-teal-700/10 border border-teal-500/20 rounded-xl px-3.5 py-3">
                          <p className="font-semibold text-sm text-teal-400 mb-1">Recommended answer:</p>
                          <p className="text-sm text-foreground leading-relaxed">{msg.data.recommended_answer}</p>
                          <div className="flex gap-2 mt-2.5">
                            <Button
                              size="sm"
                              className="h-8 text-sm bg-primary text-primary-foreground hover:bg-primary/90 px-4"
                              onClick={() => handleUseThis(msg.data.recommended_answer)}
                              data-testid="reggie-use-this"
                            >
                              {copied ? <><Check className="h-3.5 w-3.5 mr-1" />Done</> : 'Use This'}
                            </Button>
                            <Button
                              size="sm"
                              variant="ghost"
                              className="h-8 text-sm px-2"
                              onClick={() => handleCopyAnswer(msg.data.recommended_answer)}
                            >
                              <Copy className="h-3.5 w-3.5" />
                            </Button>
                          </div>
                        </div>
                      )}

                      {/* Next step */}
                      {msg.data.next_step && (
                        <div className="px-3.5 py-1.5">
                          <p className="text-sm text-muted-foreground"><span className="font-medium">Next:</span> {msg.data.next_step}</p>
                        </div>
                      )}

                      {/* Regenerate */}
                      {i === messages.length - 1 && msg.data.recommended_answer && (
                        <div className="px-3.5">
                          <button
                            onClick={handleRegenerate}
                            disabled={loading}
                            className="text-sm text-muted-foreground hover:text-teal-400 flex items-center gap-1.5 transition-colors"
                            data-testid="reggie-regenerate"
                          >
                            <RefreshCw className={`h-3.5 w-3.5 ${loading ? 'animate-spin' : ''}`} />
                            Regenerate
                          </button>
                        </div>
                      )}
                    </div>
                  )}
                </div>
              ))}

              {loading && (
                <div className="flex items-center gap-2 py-2">
                  <div className="w-6 h-6 rounded-full overflow-hidden flex-shrink-0">
                    <img src={REGGIE_IMG} alt="" className="w-full h-full rounded-full" style={{objectFit: 'cover', objectPosition: 'center 15%'}} />
                  </div>
                  <div className="flex items-center gap-1.5">
                    <Loader2 className="h-3.5 w-3.5 animate-spin text-teal-400" />
                    <span className="text-xs text-muted-foreground">Thinking...</span>
                  </div>
                </div>
              )}

              <div ref={messagesEndRef} />
            </div>

            {/* Input */}
            <form onSubmit={handleSubmit} className="flex-shrink-0 border-t border-border px-3 py-2.5 bg-secondary/20">
              <div className="flex items-center gap-2">
                <input
                  ref={inputRef}
                  type="text"
                  value={input}
                  onChange={(e) => setInput(e.target.value)}
                  placeholder="Ask Reggie anything..."
                  className="flex-1 bg-background border border-border rounded-lg px-3 py-2 text-sm text-foreground placeholder:text-muted-foreground focus:outline-none focus:ring-2 focus:ring-ring"
                  disabled={loading}
                  data-testid="reggie-input"
                />
                <Button
                  type="submit"
                  size="sm"
                  disabled={!input.trim() || loading}
                  className="h-9 w-9 p-0 bg-primary text-primary-foreground hover:bg-primary/90"
                  data-testid="reggie-send"
                >
                  <Send className="h-4 w-4" />
                </Button>
              </div>
            </form>
          </motion.div>
        )}
      </AnimatePresence>
    </>
  );
};
